import type {
  Paginated,
  PaginatedQuery,
  UpdateUserPayload,
  UserListItem,
} from '@atlas/api-contract';

import { apiClient } from './client';

/**
 * `/v1/users` — the admin roster (spec R7.7).
 *
 * Thin, like every other request module here. Only an Admin can reach these
 * endpoints; the API answers `403` for anyone else, and the UI merely hides the
 * screen as a convenience (spec R7.8).
 */

interface ReadOptions {
  signal?: AbortSignal | undefined;
}

const withSignal = ({ signal }: ReadOptions) =>
  signal === undefined ? {} : { signal };

/** One page of users, filtered server-side. */
export const listUsers = async (
  query: PaginatedQuery,
  options: ReadOptions = {},
): Promise<Paginated<UserListItem>> => {
  const { data } = await apiClient.get<Paginated<UserListItem>>('/users', {
    params: query,
    ...withSignal(options),
  });

  return data;
};

/**
 * `PATCH /v1/users/:id` — change a role or the active flag.
 *
 * The API is the real gate: it answers `403` for a self-role-change and `409`
 * for demoting or deactivating the last active Admin (spec R7.7). The Users
 * screen surfaces those inline rather than pre-checking them.
 */
export const updateUser = async (
  userId: string,
  payload: UpdateUserPayload,
): Promise<UserListItem> => {
  const { data } = await apiClient.patch<UserListItem>(
    `/users/${encodeURIComponent(userId)}`,
    payload,
  );

  return data;
};
